import isArrayHasItems from "web/utils/data/validator/array/isArrayHasItems";

import { BankResetItems } from "web/types/Banks";

import { BANK_PAYMENTS_TYPE, ONLINE_PAYMENTS_TYPE } from "./consts";
import { TBankCountsForPaymentSortContext } from "./getBankCountForPaymentSortContext";
import sortPaymentSettings from "./sortPaymentSettings";
import {
  ProductDataWithProcessedPaymentSettings,
  ProductSeparatedWithTypes,
  ProductToSeparateWithPaymentSettings,
} from "./types";

const getProductsPaymentSettingsData = (
  productsToSeparateAutomatically: ProductToSeparateWithPaymentSettings[],
  bankMappedWithBalances: Record<string, string>,
  name: string,
  bankCountsForPaymentSortContext: TBankCountsForPaymentSortContext,
  bankResets: BankResetItems
) =>
  productsToSeparateAutomatically.reduce<{
    banksBalances: Record<string, string | number>;
    items: ProductSeparatedWithTypes[];
  }>(
    (result, current, index, sourceArray) => {
      const { sku, paymentSettings } = current;

      if (isArrayHasItems(paymentSettings)) {
        let banksBalances = {
          ...result.banksBalances,
        };

        const paymentSettingsSorted = sortPaymentSettings({
          paymentSettings,
          sourceArray,
          current,
          bankCountsForPaymentSortContext,
          bankResets,
        });

        const productDataWithProcessedPaymentSettings =
          paymentSettingsSorted.reduce<ProductDataWithProcessedPaymentSettings>(
            (subResult, subCurrent) => {
              switch (true) {
                case !subResult.productPriceTotal: {
                  return subResult;
                }

                case subCurrent.type === ONLINE_PAYMENTS_TYPE: {
                  return {
                    productPriceTotal: 0,
                    productPaymentSettingsProcessed: [
                      ...subResult.productPaymentSettingsProcessed,
                      {
                        type: subCurrent.type,
                        code: subCurrent.code,
                        value: subResult.productPriceTotal,
                      },
                    ],
                  };
                }

                case subCurrent.type === BANK_PAYMENTS_TYPE: {
                  const currentBankBalance =
                    banksBalances &&
                    Object.prototype.hasOwnProperty.call(
                      banksBalances,
                      subCurrent.code
                    )
                      ? banksBalances[subCurrent.code]
                      : 0;

                  if (subResult.productPriceTotal && currentBankBalance) {
                    if (
                      subResult.productPriceTotal >=
                      (currentBankBalance as number)
                    ) {
                      banksBalances = {
                        ...banksBalances,
                        [subCurrent.code]: 0,
                      };

                      return {
                        productPriceTotal: +(
                          subResult.productPriceTotal -
                          (currentBankBalance as number)
                        ).toFixed(2),
                        productPaymentSettingsProcessed: [
                          ...subResult.productPaymentSettingsProcessed,
                          {
                            type: subCurrent.type,
                            code: subCurrent.code,
                            value: currentBankBalance,
                          },
                        ],
                      } as ProductDataWithProcessedPaymentSettings;
                    }

                    banksBalances = {
                      ...banksBalances,
                      [subCurrent.code]: +(
                        (currentBankBalance as number) -
                        subResult.productPriceTotal
                      ).toFixed(2),
                    };

                    return {
                      productPriceTotal: 0,
                      productPaymentSettingsProcessed: [
                        ...subResult.productPaymentSettingsProcessed,
                        {
                          type: subCurrent.type,
                          code: subCurrent.code,
                          value: subResult.productPriceTotal,
                        },
                      ],
                    };
                  }

                  return subResult;
                }

                default: {
                  return subResult;
                }
              }
            },
            {
              productPriceTotal: current[
                name as keyof ProductToSeparateWithPaymentSettings
              ] as unknown as number,
              productPaymentSettingsProcessed: [],
            } as ProductDataWithProcessedPaymentSettings
          );

        return {
          banksBalances,
          items: [
            ...result.items,
            {
              sku,
              payment_settings:
                productDataWithProcessedPaymentSettings.productPaymentSettingsProcessed,
            },
          ],
        } as {
          banksBalances: Record<string, string | number>;
          items: ProductSeparatedWithTypes[];
        };
      }

      return result;
    },
    {
      banksBalances: bankMappedWithBalances,
      items: [],
    }
  );

export default getProductsPaymentSettingsData;
